// Phase 3 — Stripe Checkout (hosted) launcher + return-polling.
//
// Replaces the pre-2026-06 Razorpay Standard Checkout flow. The
// Stripe flow is a full-page redirect, NOT a modal:
//   1. POST /api/cases/{id}/create-order with {origin: window.location.origin}
//      → backend returns {checkout_url, session_id}.
//   2. window.location.href = checkout_url (Stripe-hosted card form).
//   3. Stripe redirects back to {origin}/cases/{id}?session_id=cs_test_...
//   4. ChatPage detects ?session_id=… in the URL on mount and calls
//      `pollPaymentStatus(caseId, sessionId, onPaid, onError)` which
//      polls GET /api/cases/{id}/payment-status/{session_id} until
//      payment_status === "paid" (or timeout / 3 errors).
//
// SECURITY: the success_url query string is NEVER trusted as proof
// of payment. The poll endpoint queries Stripe live; the backend
// only flips case status to PAID_HANDED_OFF after Stripe says
// payment_status == "paid". The webhook is the durable
// source-of-truth — the poll is a UX accelerator.
import * as api from "./api";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 30;        // 60s total
const POLL_BACKOFF_ON_ERROR = 1.5;   // gentle exponential

/**
 * Start the Stripe Checkout flow.
 *
 * @param {object} opts
 * @param {string} opts.caseId   - case_id to bind the session to
 * @param {(msg:string)=>void} opts.onError - called if the create call fails
 */
export async function launchStripeCheckout({ caseId, onError } = {}) {
  if (!caseId) {
    onError?.("Internal error: missing case id.");
    return;
  }
  try {
    const { checkout_url } = await api.createStripeCheckout(
      caseId,
      window.location.origin,
    );
    if (!checkout_url) {
      onError?.("Couldn't reach the payment gateway — please try again.");
      return;
    }
    // Full-page redirect — Stripe will return us via success_url
    // with ?session_id=cs_test_... appended.
    window.location.href = checkout_url;
  } catch (e) {
    const msg = e?.response?.data?.detail
      || "Couldn't start the payment — please try again.";
    onError?.(msg);
  }
}

/**
 * Poll the payment-status endpoint until paid / timeout. Resolves
 * with the final status object; rejects on hard error.
 *
 * @param {string} caseId
 * @param {string} sessionId
 * @param {object} [opts]
 * @param {number} [opts.maxAttempts=POLL_MAX_ATTEMPTS]
 * @param {number} [opts.intervalMs=POLL_INTERVAL_MS]
 */
export async function pollPaymentStatus(caseId, sessionId, opts = {}) {
  const maxAttempts = opts.maxAttempts ?? POLL_MAX_ATTEMPTS;
  let intervalMs = opts.intervalMs ?? POLL_INTERVAL_MS;
  let errors = 0;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const res = await api.getPaymentStatus(caseId, sessionId);
      if (res.payment_status === "paid") {
        return { ok: true, ...res };
      }
      if (res.status === "expired" || res.payment_status === "failed") {
        return { ok: false, ...res };
      }
    } catch (e) {
      errors += 1;
      if (errors >= 3) {
        return {
          ok: false,
          error: e?.response?.data?.detail
            || "We couldn't check payment status — please refresh.",
        };
      }
      intervalMs = Math.min(intervalMs * POLL_BACKOFF_ON_ERROR, 8000);
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { ok: false, timeout: true };
}

/**
 * Strip ?session_id=… from the current URL (called after the poll
 * resolves so a refresh doesn't trigger a second poll cycle).
 */
export function clearSessionFromUrl() {
  const url = new URL(window.location.href);
  if (url.searchParams.has("session_id")) {
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.toString());
  }
}
